import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { generateLocalizedUrl } from '@/lib/slugUtils';
import { supportedLanguages, type SupportedLanguage } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import Breadcrumb from '@/components/Breadcrumb';
import PageSpecificFAQStructuredData from '@/components/PageSpecificFAQStructuredData';
import { 
  Calculator, 
  TrendingUp,
  Users, 
  Target,
  Clock,
  Sparkles,
  Shield,
  Zap,
  Award,
  ArrowRight,
  Check,
  ChevronRight,
  CheckCircle2,
  BarChart3,
  Database,
  Share2,
  FileText,
  Lightbulb
} from 'lucide-react';

const whyIcons = [Sparkles, Target, Zap, Database, TrendingUp];
const howToIcons = [Lightbulb, FileText, Clock, ArrowRight, Users];
const seoIcons = [Clock, Share2, Award, Target];
const whyPriceFormsIcons = [Zap, Target, Users, BarChart3, Shield];

export default function B2BLeadGen() {
  const { t, i18n } = useTranslation('b2b-lead-gen');
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';

  const handleCTAClick = () => {
    const contactUrl = generateLocalizedUrl('contact', currentLang);
    navigate(contactUrl);
  };

  return (
    <>
      <SEOHead
        title={t('meta.title')}
        description={t('meta.description')}
        lang={currentLang}
        path={generateLocalizedUrl('b2b-lead-gen', currentLang)}
      />
      
      <Header />
      
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/10">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          <Breadcrumb />

          {/* Hero Section */}
          <section className="text-center mb-16 mt-12">
            <Badge variant="secondary" className="mb-6 text-base px-6 py-2" data-testid="badge-hero">
              <Target className="w-4 h-4 mr-2 inline" />
              {t('hero.badge')}
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent" data-testid="title-hero">
              {t('hero.title')}
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground mb-4 font-medium" data-testid="text-subtitle">
              {t('hero.subtitle')}
            </p>
            
            <p className="text-lg text-muted-foreground mb-4 max-w-3xl mx-auto" data-testid="text-intro">
              {t('hero.intro')}
            </p>
            
            <p className="text-lg text-muted-foreground mb-4 max-w-3xl mx-auto" data-testid="text-description">
              {t('hero.description')}
            </p>
            
            <p className="text-lg text-foreground mb-8 max-w-3xl mx-auto font-medium" data-testid="text-conclusion">
              {t('hero.conclusion')}
            </p>
            
            <Button 
              size="lg" 
              className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
              onClick={handleCTAClick}
              data-testid="button-hero-cta"
            >
              {t('hero.cta')} <ArrowRight className="ml-2" />
            </Button>
          </section>

          {/* What Section */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center" data-testid="title-what">
              {t('what.title')}
            </h2>
            
            <div className="prose prose-lg max-w-none dark:prose-invert">
              <p className="text-lg text-muted-foreground mb-4" data-testid="text-what-description">
                {t('what.description')}
              </p>
              
              <p className="text-lg text-muted-foreground mb-4" data-testid="text-what-explanation">
                {t('what.explanation')}
              </p>
              
              <p className="text-lg text-foreground font-medium mb-3" data-testid="text-what-conclusion">
                {t('what.conclusion')}
              </p>
              
              <ul className="space-y-2 mb-6">
                {(t('what.benefits', { returnObjects: true }) as string[]).map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2" data-testid={`item-what-benefit-${index}`}>
                    <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <span className="text-lg">{benefit}</span>
                  </li>
                ))}
              </ul>
              
              <p className="text-lg text-muted-foreground" data-testid="text-what-outro">
                {t('what.outro')}
              </p>
            </div>
          </section>

          {/* Why Section */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" data-testid="title-why">
              {t('why.title')}
            </h2>
            
            <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto" data-testid="text-why-description">
              {t('why.description')}
            </p>
            
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {(t('why.reasons', { returnObjects: true }) as Array<{ title: string; description: string }>).map((reason, index) => {
                const Icon = whyIcons[index % whyIcons.length];
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow" data-testid={`card-why-${index}`}>
                    <CardContent className="p-6">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold mb-3" data-testid={`title-why-${index}`}>
                        {reason.title}
                      </h3>
                      <p className="text-muted-foreground" data-testid={`text-why-${index}`}>
                        {reason.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Facts Table Section */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center" data-testid="title-facts">
              {t('facts.title')}
            </h2>
            
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="table-facts">
                  <thead className="bg-primary/5">
                    <tr>
                      <th className="text-left p-4 font-semibold" data-testid="header-facts-type">
                        {t('facts.table.headers.type')}
                      </th>
                      <th className="text-left p-4 font-semibold" data-testid="header-facts-conversion">
                        {t('facts.table.headers.conversion')}
                      </th>
                      <th className="text-left p-4 font-semibold" data-testid="header-facts-quality">
                        {t('facts.table.headers.quality')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(t('facts.table.rows', { returnObjects: true }) as Array<{ type: string; conversion: string; quality: string }>).map((row, index) => (
                      <tr key={index} className="border-t" data-testid={`row-facts-${index}`}>
                        <td className="p-4" data-testid={`cell-facts-type-${index}`}>{row.type}</td>
                        <td className="p-4 font-semibold" data-testid={`cell-facts-conversion-${index}`}>{row.conversion}</td>
                        <td className="p-4" data-testid={`cell-facts-quality-${index}`}>{row.quality}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </section>

          {/* How To Section */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" data-testid="title-howto">
              {t('howTo.title')}
            </h2>
            
            <div className="space-y-6">
              {(t('howTo.steps', { returnObjects: true }) as Array<{ number: string; title: string; description: string }>).map((step, index) => {
                const Icon = howToIcons[index % howToIcons.length];
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow" data-testid={`card-howto-${index}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-2xl font-bold text-primary-foreground" data-testid={`number-howto-${index}`}>
                            {step.number}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold mb-2 flex items-center gap-2" data-testid={`title-howto-${index}`}>
                            <Icon className="w-5 h-5 text-primary" />
                            {step.title}
                          </h3>
                          <p className="text-muted-foreground" data-testid={`text-howto-${index}`}>
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Case Study Section */}
          <section className="mb-16">
            <Card className="bg-primary/5 border-primary/20" data-testid="card-case">
              <CardContent className="p-8">
                <h2 className="text-3xl md:text-4xl font-bold mb-6" data-testid="title-case">
                  {t('case.title')}
                </h2>
                
                <p className="text-lg text-muted-foreground mb-6" data-testid="text-case-description">
                  {t('case.description')}
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  {(t('case.results', { returnObjects: true }) as string[]).map((result, index) => (
                    <div key={index} className="flex items-start gap-2" data-testid={`item-case-result-${index}`}>
                      <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <span className="font-medium">{result}</span>
                    </div>
                  ))}
                </div>
                
                <blockquote className="border-l-4 border-primary pl-6 italic text-lg mb-4" data-testid="quote-case">
                  "{t('case.quote')}"
                  <footer className="text-sm text-muted-foreground mt-2 not-italic" data-testid="text-case-author">
                    — {t('case.author')}
                  </footer>
                </blockquote>
                
                <p className="text-lg text-foreground font-medium" data-testid="text-case-conclusion">
                  {t('case.conclusion')}
                </p>
              </CardContent>
            </Card>
          </section>

          {/* KPI Table Section */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center" data-testid="title-kpi">
              {t('kpi.title')}
            </h2>
            
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="table-kpi">
                  <thead className="bg-primary/5">
                    <tr>
                      <th className="text-left p-4 font-semibold" data-testid="header-kpi-metric">
                        {t('kpi.table.headers.metric')}
                      </th>
                      <th className="text-left p-4 font-semibold" data-testid="header-kpi-before">
                        {t('kpi.table.headers.before')}
                      </th>
                      <th className="text-left p-4 font-semibold" data-testid="header-kpi-after">
                        {t('kpi.table.headers.after')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(t('kpi.table.rows', { returnObjects: true }) as Array<{ metric: string; before: string; after: string }>).map((row, index) => (
                      <tr key={index} className="border-t" data-testid={`row-kpi-${index}`}>
                        <td className="p-4 font-medium" data-testid={`cell-kpi-metric-${index}`}>{row.metric}</td>
                        <td className="p-4" data-testid={`cell-kpi-before-${index}`}>{row.before}</td>
                        <td className="p-4 font-semibold text-primary" data-testid={`cell-kpi-after-${index}`}>{row.after}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </section>

          {/* SEO Benefits Section */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" data-testid="title-seo">
              {t('seo.title')}
            </h2>
            
            <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto" data-testid="text-seo-description">
              {t('seo.description')}
            </p>
            
            <div className="grid gap-6 md:grid-cols-2">
              {(t('seo.benefits', { returnObjects: true }) as Array<{ title: string; description: string }>).map((benefit, index) => {
                const Icon = seoIcons[index % seoIcons.length];
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow" data-testid={`card-seo-${index}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold mb-2" data-testid={`title-seo-${index}`}>
                            {benefit.title}
                          </h3>
                          <p className="text-muted-foreground" data-testid={`text-seo-${index}`}>
                            {benefit.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Why PriceForms Section */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" data-testid="title-whypriceforms">
              {t('whyPriceForms.title')}
            </h2>
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {(t('whyPriceForms.features', { returnObjects: true }) as string[]).map((feature, index) => {
                const Icon = whyPriceFormsIcons[index % whyPriceFormsIcons.length];
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow" data-testid={`card-feature-${index}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <Icon className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                        <span className="font-medium" data-testid={`text-feature-${index}`}>{feature}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" data-testid="title-faq">
              {t('faq.title')}
            </h2>
            
            <div className="space-y-6 max-w-3xl mx-auto">
              {(t('faq.items', { returnObjects: true }) as Array<{ question: string; answer: string }>).map((item, index) => (
                <Card key={index} data-testid={`card-faq-${index}`}>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-3 flex items-start gap-2" data-testid={`question-faq-${index}`}>
                      <ChevronRight className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      {item.question}
                    </h3>
                    <p className="text-muted-foreground pl-7" data-testid={`answer-faq-${index}`}>
                      {item.answer}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Final CTA Section */}
          <section className="text-center py-16 px-6 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5">
            <h2 className="text-3xl md:text-5xl font-bold mb-6" data-testid="title-cta">
              {t('cta.title')}
            </h2>
            
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto" data-testid="text-cta">
              {t('cta.description')}
            </p>
            
            <Button 
              size="lg" 
              className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
              onClick={handleCTAClick}
              data-testid="button-cta"
            >
              {t('cta.button')} <ArrowRight className="ml-2" />
            </Button>
          </section>
        </div>
      </div>
      
      <PageSpecificFAQStructuredData namespace="b2b-lead-gen" faqKey="faq.items" />
      <Footer />
    </>
  );
}
